import type { BarcodeDetector as PonyfillDetector, BarcodeFormat } from 'barcode-detector/ponyfill'

/** What the camera reads: QR (Connect codes) and the 1D codes on products, cards and receipts (POS.md §18.8). */
export const CAMERA_FORMATS: BarcodeFormat[] = ['qr_code', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf']

export interface CameraDetector {
  detect: (source: CanvasImageSource) => Promise<{ rawValue: string; format: string }[]>
}

let zxing: Promise<CameraDetector> | null = null
let preferred: Promise<CameraDetector> | null = null

/** ZXing in WebAssembly, shipped with pos-web (not the package's CDN default) so it works offline once cached. */
export const getZxingDetector = (): Promise<CameraDetector> => {
  zxing ??= (async () => {
    const [{ BarcodeDetector, prepareZXingModule }, { default: wasmUrl }] = await Promise.all([
      import('barcode-detector/ponyfill'),
      import('zxing-wasm/reader/zxing_reader.wasm?url'),
    ])
    prepareZXingModule({
      overrides: { locateFile: (path: string, prefix: string) => (path.endsWith('.wasm') ? wasmUrl : prefix + path) },
    })
    return new BarcodeDetector({ formats: CAMERA_FORMATS })
  })()
  zxing.catch(() => (zxing = null))
  return zxing
}

/** The browser's own BarcodeDetector where it reads QR and EAN (Android Chrome, macOS), else ZXing. */
export const getCameraDetector = (): Promise<CameraDetector> => {
  preferred ??= (async () => {
    const Native = (globalThis as { BarcodeDetector?: typeof PonyfillDetector }).BarcodeDetector
    if (Native) {
      const supported = await Native.getSupportedFormats().catch(() => [] as BarcodeFormat[])
      const formats = CAMERA_FORMATS.filter((f) => supported.includes(f))
      if (formats.includes('qr_code') && formats.includes('ean_13')) return new Native({ formats })
    }
    return getZxingDetector()
  })()
  preferred.catch(() => (preferred = null))
  return preferred
}

/**
 * The part of the camera frame the user sees in an `object-cover` box, at the camera's own resolution: what is
 * decoded, so a code outside the box is not read and the reader never gets a scaled-down frame.
 */
export const visibleFrame = (video: HTMLVideoElement, canvas: HTMLCanvasElement): HTMLCanvasElement | null => {
  const vw = video.videoWidth
  const vh = video.videoHeight
  const ew = video.clientWidth
  const eh = video.clientHeight
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!vw || !vh || !ew || !eh || !ctx) return null
  const scale = Math.max(ew / vw, eh / vh)
  const sw = Math.min(vw, ew / scale)
  const sh = Math.min(vh, eh / scale)
  canvas.width = Math.round(sw)
  canvas.height = Math.round(sh)
  ctx.drawImage(video, (vw - sw) / 2, (vh - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height)
  return canvas
}

/** Camera access needs HTTPS or localhost; a LAN address over plain http has none (see `.claude` conventions). */
export const cameraAvailable = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia

/**
 * How much the middle of the frame looks like a code: mean left–right brightness change across a few rows. Bars and
 * QR modules score high; a wall, a hand or a blurred view scores low. Used to open the large view before a full read.
 */
export const stripeScore = (video: HTMLVideoElement, canvas: HTMLCanvasElement): number => {
  const w = 160
  const h = 48
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx || !video.videoWidth) return 0
  canvas.width = w
  canvas.height = h
  const cropH = video.videoHeight * 0.35
  ctx.drawImage(video, 0, (video.videoHeight - cropH) / 2, video.videoWidth, cropH, 0, 0, w, h)
  const { data } = ctx.getImageData(0, 0, w, h)
  let sum = 0
  let n = 0
  for (let y = 4; y < h; y += 8) {
    let prev = -1
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114
      if (prev >= 0) {
        sum += Math.abs(lum - prev)
        n++
      }
      prev = lum
    }
  }
  return n ? sum / n : 0
}
